import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Clock, DollarSign, CheckCircle, X, Calendar, Star, ChevronDown, ChevronUp, Repeat, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";

interface Application {
  id: string;
  status: string;
  created_at: string;
  message: string;
  course_id: string;
  course: {
    id: string;
    title: string;
    price: number;
    course_type: string;
    included_sessions?: number;
  };
  mentee: {
    id: string;
    first_name: string;
    last_name: string;
    avatar_url: string;
  };
  remaining_sessions?: number;
  has_booking?: boolean;
  isSubscriptionCard?: boolean;
  subscription_id?: string;
  subscription_application_id?: string;
  period_start_date?: string;
  period_end_date?: string;
  total_allowance?: number;
  bookings_used?: number;
}

interface CompletedSession {
  id: string;
  booked_date: string;
  start_time: string;
  end_time: string;
  notes?: string;
  application_id?: string;
  mentee: {
    id: string;
    first_name: string;
    last_name: string;
    avatar_url?: string;
  };
  mentor_review?: {
    id: string;
    rating: number;
    review_text?: string;
    created_at: string;
  };
  course?: {
    title: string;
    price: number;
  };
}

interface CompletedSubscription {
  id: string;
  application_id?: string;
  subscription_end_date: string;
  course_title: string;
  course_price: number;
  mentee: {
    id: string;
    first_name: string;
    last_name: string;
    avatar_url?: string;
  };
  mentor_review?: {
    id: string;
    rating: number;
    review_text?: string;
    created_at: string;
  };
}

interface GroupedCompletedItems {
  [menteeId: string]: {
    mentee: {
      id: string;
      first_name: string;
      last_name: string;
      avatar_url?: string;
    };
    sessions: CompletedSession[];
    subscriptions: CompletedSubscription[];
  };
}

const MentorApplications = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [applications, setApplications] = useState<Application[]>([]);
  const [subscriptionBookingCards, setSubscriptionBookingCards] = useState<Application[]>([]);
  const [completedSessions, setCompletedSessions] = useState<CompletedSession[]>([]);
  const [completedSubscriptions, setCompletedSubscriptions] = useState<CompletedSubscription[]>([]);
  const [groupedCompletedItems, setGroupedCompletedItems] = useState<GroupedCompletedItems>({});
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [allExpanded, setAllExpanded] = useState(false);

  useEffect(() => {
    if (user) {
      fetchApplications();
      fetchCompletedItems();
      fetchSubscriptionBookings();
    }
  }, [user]);

  useEffect(() => {
    if (!user?.id) return;

    const handleSubscriptionBookingUpdate = (event: CustomEvent) => {
      console.log('DEBUG: Mentor - Received subscription-booking-updated event:', event.detail);
      if (event.detail.mentorId === user.id) {
        console.log('DEBUG: Mentor - Refreshing applications due to subscription booking update');
        fetchApplications();
        fetchSubscriptionBookings();
      }
    };

    const handleBookingCompleted = (event: CustomEvent) => {
      console.log('DEBUG: Mentor - Received booking-completed event:', event.detail);
      // Refresh applications and subscription bookings
      fetchApplications();
      fetchSubscriptionBookings();
      fetchCompletedItems();
    };

    const handleSubscriptionEnded = (event: CustomEvent) => {
      console.log('DEBUG: Mentor - Received subscription-ended event:', event.detail);
      if (event.detail.mentorId === user.id) {
        console.log('DEBUG: Mentor - Refreshing applications due to subscription end');
        // Refresh applications and subscription bookings
        fetchApplications();
        fetchSubscriptionBookings();
      }
    };

    const handleReviewSubmitted = (event: CustomEvent) => {
      console.log('DEBUG: Mentor - Received review-submitted event:', event.detail);
      if (event.detail.mentorId === user.id) {
        console.log('DEBUG: Mentor - Refreshing completed items due to review submission');
        // Refresh completed items to show newly completed subscriptions
        fetchCompletedItems();
      }
    };

    const handleBookingChangeRequested = (event: CustomEvent) => {
      console.log('DEBUG: Mentor - Received booking-change-requested event:', event.detail);
      if (event.detail.mentorId === user.id) {
        console.log('DEBUG: Mentor - Refreshing due to booking change request');
        fetchApplications();
        fetchSubscriptionBookings();
      }
    };

    window.addEventListener('subscription-booking-updated', handleSubscriptionBookingUpdate as EventListener);
    window.addEventListener('booking-completed', handleBookingCompleted as EventListener);
    window.addEventListener('subscription-ended', handleSubscriptionEnded as EventListener);
    window.addEventListener('review-submitted', handleReviewSubmitted as EventListener);
    window.addEventListener('booking-change-requested', handleBookingChangeRequested as EventListener);

    return () => {
      window.removeEventListener('subscription-booking-updated', handleSubscriptionBookingUpdate as EventListener);
      window.removeEventListener('booking-completed', handleBookingCompleted as EventListener);
      window.removeEventListener('subscription-ended', handleSubscriptionEnded as EventListener);
      window.removeEventListener('review-submitted', handleReviewSubmitted as EventListener);
      window.removeEventListener('booking-change-requested', handleBookingChangeRequested as EventListener);
    };
  }, [user?.id]);

  const fetchApplications = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      
      const { data: applicationsData, error } = await supabase
        .from('applications')
        .select(`
          id,
          status,
          created_at,
          message,
          course_id,
          user_id,
          remaining_sessions
        `)
        .eq('mentor_id', user.id)
        .in('status', ['pending', 'pending_mentee_purchase', 'paid', 'bookingRequired'])
        .order('created_at', { ascending: false });

      if (error) {
        console.error("ERROR: Failed to fetch applications:", error);
        throw error;
      }

      if (!applicationsData) {
        console.log("DEBUG: No applications data returned");
        setApplications([]);
        return;
      }

      console.log("DEBUG: Raw applications data:", applicationsData);
      console.log("DEBUG: Applications by status:", applicationsData.reduce((acc, app) => {
        acc[app.status] = (acc[app.status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>));

      // Use Promise.allSettled for better error handling
      const enrichmentResults = await Promise.allSettled(
        applicationsData.map(async (app) => {
          console.log(`DEBUG: Enriching application ${app.id} (status: ${app.status})`);
          
          try {
            // Fetch course details
            const { data: courseData, error: courseError } = await supabase
              .from('courses')
              .select('id, title, price, course_type, included_sessions')
              .eq('id', app.course_id)
              .single();

            if (courseError) {
              console.error(`ERROR: Failed to fetch course ${app.course_id} for application ${app.id}:`, courseError);
              return null;
            }

            if (!courseData) {
              console.warn(`WARNING: No course data found for course_id ${app.course_id} (application ${app.id})`);
              return null;
            }

            // Fetch mentee details
            const { data: menteeData, error: menteeError } = await supabase
              .from('profiles')
              .select('id, first_name, last_name, avatar_url')
              .eq('id', app.user_id)
              .single();

            if (menteeError) {
              console.error(`ERROR: Failed to fetch mentee ${app.user_id} for application ${app.id}:`, menteeError);
              return null;
            }

            if (!menteeData) {
              console.warn(`WARNING: No mentee data found for user_id ${app.user_id} (application ${app.id})`);
              return null;
            }

            // Check if there are any session bookings for this application
            const { data: bookingData, error: bookingError } = await supabase
              .from('session_bookings')
              .select('id')
              .eq('application_id', app.id)
              .limit(1);

            if (bookingError) {
              console.error(`ERROR: Failed to check bookings for application ${app.id}:`, bookingError);
              // Don't return null here, bookings check is not critical
            }

            const enrichedApp = {
              ...app,
              course: courseData,
              mentee: menteeData,
              has_booking: bookingData && bookingData.length > 0
            };

            console.log(`DEBUG: Successfully enriched application ${app.id}:`, {
              status: app.status,
              course_title: courseData.title,
              mentee_name: `${menteeData.first_name} ${menteeData.last_name}`,
              has_booking: enrichedApp.has_booking
            });

            return enrichedApp;
          } catch (error) {
            console.error(`ERROR: Exception while enriching application ${app.id}:`, error);
            return null;
          }
        })
      );

      // Process results and separate successful from failed enrichments
      const validApplications: Application[] = [];
      const failedEnrichments: string[] = [];

      enrichmentResults.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value) {
          validApplications.push(result.value);
        } else {
          const appId = applicationsData[index].id;
          failedEnrichments.push(appId);
          console.error(`ERROR: Failed to enrich application ${appId}:`, 
            result.status === 'rejected' ? result.reason : 'Null result');
        }
      });

      console.log(`DEBUG: Successfully enriched ${validApplications.length}/${applicationsData.length} applications`);
      if (failedEnrichments.length > 0) {
        console.warn(`WARNING: Failed to enrich applications: ${failedEnrichments.join(', ')}`);
        toast({
          title: "Partial Data Load",
          description: `Some application data could not be loaded (${failedEnrichments.length} failed)`,
          variant: "destructive",
        });
      }

      // Log applications by status after enrichment
      const statusCounts = validApplications.reduce((acc, app) => {
        acc[app.status] = (acc[app.status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      console.log("DEBUG: Enriched applications by status:", statusCounts);
      console.log("DEBUG: Pending applications:", validApplications.filter(app => app.status === 'pending'));
      
      setApplications(validApplications);
    } catch (error) {
      console.error("ERROR: Exception in fetchApplications:", error);
      toast({
        title: "Error",
        description: "Failed to load applications. Please try again.",
        variant: "destructive",
      });
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionBookings = async () => {
    if (!user?.id) return;

    try {
      console.log("DEBUG: Mentor - Starting fetchSubscriptionBookings for mentor:", user.id);
      
      // Get ALL subscription bookings for this mentor with available slots
      const { data: subscriptionBookings, error } = await supabase
        .from('subscription_bookings')
        .select(`
          id,
          application_id,
          mentor_id,
          mentee_id,
          total_allowance,
          bookings_used,
          week_start_date,
          period_end_date,
          is_active
        `)
        .eq('mentor_id', user.id);

      console.log("DEBUG: Mentor - All subscription bookings query result:", { subscriptionBookings, error });

      if (error) {
        console.error("Mentor - Error fetching subscription bookings:", error);
        setSubscriptionBookingCards([]);
        return;
      }

      if (!subscriptionBookings || subscriptionBookings.length === 0) {
        console.log("DEBUG: Mentor - No subscription bookings found at all");
        setSubscriptionBookingCards([]);
        return;
      }

      // Filter for bookings with available slots and active subscriptions
      const today = new Date().toISOString().split('T')[0];
      const activeBookingsWithSlots = subscriptionBookings.filter(booking => {
        const hasSlots = booking.total_allowance - booking.bookings_used > 0;
        const isActive = booking.is_active === true;
        const notExpired = !booking.period_end_date || booking.period_end_date >= today;
        return hasSlots && isActive && notExpired;
      });

      const activeBookings = [];
      
      for (const booking of activeBookingsWithSlots) {
        const availableSlots = booking.total_allowance - booking.bookings_used;
        console.log(`DEBUG: Mentor - Checking booking ${booking.id} - Total: ${booking.total_allowance}, Used: ${booking.bookings_used}, Available: ${availableSlots}`);
        
        // Check if the related application/subscription is still active
        const { data: applicationData, error: appError } = await supabase
          .from('applications')
          .select('status, is_subscription_active, subscription_end_date')
          .eq('id', booking.application_id)
          .single();
          
        console.log(`DEBUG: Mentor - Application ${booking.application_id} data:`, applicationData);
          
        if (appError) {
          console.error(`DEBUG: Mentor - Error fetching application ${booking.application_id}:`, appError);
          continue;
        }
        
        // Accept applications with 'paid' status that have active subscriptions
        // or applications that are already marked as subscription active
        const isValidStatus = applicationData?.status === 'paid' || applicationData?.status === 'bookingRequired';
        const isSubscriptionActive = applicationData?.is_subscription_active === true;
        
        if (!isValidStatus && !isSubscriptionActive) {
          console.log(`DEBUG: Mentor - Skipping booking ${booking.id} - invalid status: ${applicationData?.status}, active: ${applicationData?.is_subscription_active}`);
          continue;
        }
        
        // Check if subscription hasn't ended (allow null end dates)
        if (applicationData?.subscription_end_date && new Date(applicationData.subscription_end_date) <= new Date()) {
          console.log(`DEBUG: Mentor - Skipping booking ${booking.id} - subscription has ended on ${applicationData.subscription_end_date}`);
          continue;
        }
        
        console.log(`DEBUG: Mentor - Adding active booking ${booking.id} with ${availableSlots} available slots`);
        activeBookings.push(booking);
      }
      
      console.log("DEBUG: Mentor - Active bookings with available slots:", activeBookings);
      
      if (activeBookings.length > 0) {
        await processSubscriptionBookings(activeBookings);
      } else {
        console.log("DEBUG: Mentor - No active bookings with available slots found");
        setSubscriptionBookingCards([]);
      }
    } catch (error) {
      console.error("Exception in fetchSubscriptionBookings:", error);
      setSubscriptionBookingCards([]);
    }
  };

  const processSubscriptionBookings = async (subscriptionBookings: any[]) => {
    console.log("DEBUG: Mentor - Processing", subscriptionBookings.length, "subscription booking records");

    const bookingCards: Application[] = [];
    
    for (const booking of subscriptionBookings) {
      const availableSlots = booking.total_allowance - booking.bookings_used;
      console.log(`DEBUG: Mentor - Processing booking ${booking.id} - Available slots: ${availableSlots}`);
      
      // Fetch course and mentee details
      const { data: applicationData, error: appError } = await supabase
        .from('applications')
        .select(`
          course_id,
          courses:course_id (
            id,
            title,
            price,
            course_type
          )
        `)
        .eq('id', booking.application_id)
        .single();

      if (appError) {
        console.error(`DEBUG: Mentor - Error fetching application ${booking.application_id}:`, appError);
        continue;
      }

      // Fetch mentee profile
      const { data: menteeData, error: menteeError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, avatar_url')
        .eq('id', booking.mentee_id)
        .single();

      if (menteeError) {
        console.error(`DEBUG: Mentor - Error fetching mentee ${booking.mentee_id}:`, menteeError);
        continue;
      }

      console.log(`DEBUG: Mentor - Successfully fetched data for booking ${booking.id}`);

      if (applicationData?.courses && menteeData) {
        // ✅ CREATE MULTIPLE BOOKING CARDS FOR EACH AVAILABLE SLOT
        for (let slotIndex = 0; slotIndex < availableSlots; slotIndex++) {
          const cardId = `mentor-subscription-${booking.id}-slot-${slotIndex + 1}`;
          console.log(`DEBUG: Mentor - Creating booking card ${cardId} (slot ${slotIndex + 1} of ${availableSlots})`);
          
            const bookingCard: Application = {
              id: cardId,
              status: 'bookingRequired',
              created_at: booking.week_start_date,
              message: `Weekly subscription session ${slotIndex + 1} - Awaiting booking`,
              course_id: applicationData.courses.id,
              course: {
                id: applicationData.courses.id,
                title: applicationData.courses.title,
                price: applicationData.courses.price,
                course_type: applicationData.courses.course_type,
              },
              mentee: {
                id: menteeData.id,
                first_name: menteeData.first_name,
                last_name: menteeData.last_name,
                avatar_url: menteeData.avatar_url,
              },
              isSubscriptionCard: true,
              subscription_id: booking.id,
              subscription_application_id: booking.application_id,
              period_start_date: booking.week_start_date,
              period_end_date: booking.period_end_date,
              total_allowance: booking.total_allowance,
              bookings_used: booking.bookings_used,
            };

          bookingCards.push(bookingCard);
        }
      }
    }

    console.log(`DEBUG: Mentor - Generated ${bookingCards.length} total booking cards from ${subscriptionBookings.length} subscription records`);
    console.log("DEBUG: Mentor - Booking cards details:", bookingCards.map(card => ({
      id: card.id,
      mentee: `${card.mentee.first_name} ${card.mentee.last_name}`,
      course: card.course.title,
      message: card.message
    })));
    
    setSubscriptionBookingCards(bookingCards);
  };

  const fetchCompletedItems = async () => {
    if (!user?.id) return;

    try {
      console.log('DEBUG: Fetching completed sessions and subscriptions for mentor:', user.id);
      
      // Fetch completed sessions - don't filter by application status for individual sessions
      const { data: sessionBookings, error: sessionsError } = await supabase
        .from('session_bookings')
        .select(`
          id,
          booked_date,
          start_time,
          end_time,
          notes,
          mentee_id,
          application_id
        `)
        .eq('mentor_id', user.id)
        .eq('status', 'completed')
        .eq('mentor_review_submitted', true)
        .order('booked_date', { ascending: false });

      if (sessionsError) {
        console.error('ERROR: Fetching completed sessions:', sessionsError);
        throw sessionsError;
      }

      console.log('DEBUG: Found completed sessions:', sessionBookings?.length || 0);
      console.log('DEBUG: Session bookings data:', sessionBookings?.map(s => ({ 
        id: s.id, 
        application_id: s.application_id, 
        mentee_id: s.mentee_id 
      })));

      // Fetch completed subscription applications (these need status = 'completed')
      const { data: subscriptionApplications, error: subscriptionsError } = await supabase
        .from('applications')
        .select(`
          id,
          application_id,
          subscription_end_date,
          user_id,
          course_id,
          courses (
            title,
            price
          )
        `)
        .eq('mentor_id', user.id)
        .eq('status', 'completed')
        .not('subscription_end_date', 'is', null)
        .order('subscription_end_date', { ascending: false });

      if (subscriptionsError) {
        console.error('ERROR: Fetching completed subscriptions:', subscriptionsError);
        throw subscriptionsError;
      }

      console.log('DEBUG: Found completed subscription applications:', subscriptionApplications?.length || 0);

      // Get unique mentee IDs from session bookings and fetch their profiles
      const sessionMenteeIds = sessionBookings?.map(b => b.mentee_id) || [];
      let sessionMenteeProfiles = [];
      
      if (sessionMenteeIds.length > 0) {
        const uniqueSessionMenteeIds = [...new Set(sessionMenteeIds)];
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, avatar_url')
          .in('id', uniqueSessionMenteeIds);

        if (profilesError) {
          console.error('ERROR: Fetching session mentee profiles:', profilesError);
        } else {
          sessionMenteeProfiles = profiles || [];
        }
      }

      // Process sessions with reviews and course details
      // For sessions, we need to query using application_id since subscription_reviews.booking_id stores the application ID
      const applicationIds = sessionBookings?.map(b => b.application_id).filter(Boolean) || [];
      console.log('DEBUG: Application IDs from session bookings:', applicationIds);
      let sessionReviewsData: any[] = [];
      
      if (applicationIds.length > 0) {
        // First get the text application IDs from the applications table
        const { data: applications, error: appsError } = await supabase
          .from('applications')
          .select('id, application_id')
          .in('id', applicationIds);

        if (appsError) {
          console.error('ERROR: Fetching application IDs:', appsError);
        } else {
          const textApplicationIds = applications?.map(app => app.application_id).filter(Boolean) || [];
          console.log('DEBUG: Text application IDs:', textApplicationIds);
          
          // Get session booking IDs for fetching reviews from appointment_reviews table
          const sessionBookingIds = sessionBookings?.map(booking => booking.id) || [];
          console.log('DEBUG: Session booking IDs for reviews:', sessionBookingIds.length);
          
          if (sessionBookingIds.length > 0) {
            try {
              const { data: reviews, error: reviewsError } = await (supabase as any)
                .from('appointment_reviews')
                .select('id, booking_id, reviewer_id, reviewee_id, rating, review_text, created_at')
                .in('booking_id', sessionBookingIds)
                .eq('reviewer_id', user.id);

              if (reviewsError) {
                console.error('ERROR: Fetching session reviews:', reviewsError);
              } else {
                sessionReviewsData = reviews || [];
                console.log('DEBUG: Found session reviews:', sessionReviewsData.length);
              }
            } catch (err) {
              console.error('ERROR: Query failed:', err);
              sessionReviewsData = [];
            }
          }
        }
      }

      // Get course details for sessions
      let sessionCoursesData = [];
      
      if (applicationIds.length > 0) {
        const { data: applications, error: appsError } = await supabase
          .from('applications')
          .select(`
            id,
            application_id,
            course_id,
            courses (
              title,
              price
            )
          `)
          .in('id', applicationIds);

        if (appsError) {
          console.error('ERROR: Fetching session course details:', appsError);
        } else {
          sessionCoursesData = applications || [];
          console.log('DEBUG: Session courses data:', sessionCoursesData?.map(a => ({ 
            id: a.id, 
            application_id: a.application_id, 
            course_title: a.courses?.title 
          })));
        }
      }

      // Process subscriptions with reviews and mentee details
      const subscriptionIds = subscriptionApplications?.map(s => s.id) || [];
      let subscriptionReviewsData: any[] = [];
      
      if (subscriptionIds.length > 0) {
        // First get the text application IDs from the applications table for subscriptions
        const { data: subApplications, error: subAppsError } = await supabase
          .from('applications')
          .select('id, application_id')
          .in('id', subscriptionIds);

        if (subAppsError) {
          console.error('ERROR: Fetching subscription application IDs:', subAppsError);
        } else {
          const textSubApplicationIds = subApplications?.map(app => app.application_id).filter(Boolean) || [];
          console.log('DEBUG: Text subscription application IDs:', textSubApplicationIds);
          
          if (textSubApplicationIds.length > 0) {
            const { data: reviews, error: reviewsError } = await (supabase as any)
              .from('subscription_reviews')
              .select('id, application_id, reviewer_id, reviewee_id, rating, review_text, created_at')
              .in('application_id', textSubApplicationIds)
              .eq('reviewer_id', user.id)
              .eq('review_type', 'mentor_to_mentee');

            if (reviewsError) {
              console.error('ERROR: Fetching subscription reviews:', reviewsError);
            } else {
              subscriptionReviewsData = reviews || [];
            }
          }
        }
      }

      // Get mentee profiles for subscriptions
      const menteeIds = subscriptionApplications?.map(s => s.user_id) || [];
      let menteeProfilesData = [];
      
      if (menteeIds.length > 0) {
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, avatar_url')
          .in('id', menteeIds);

        if (profilesError) {
          console.error('ERROR: Fetching mentee profiles:', profilesError);
        } else {
          menteeProfilesData = profiles || [];
        }
      }

      // Combine sessions with their reviews and course details
      // Match reviews using session booking ID as the key
      const completedSessionsWithReviews = sessionBookings?.map(session => {
        const appData = sessionCoursesData.find(app => app.id === session.application_id);
        const review = sessionReviewsData.find(review => review.booking_id === session.id);
        const menteeProfile = sessionMenteeProfiles.find(p => p.id === session.mentee_id);
        
        console.log(`DEBUG: Processing session ${session.id}:`, {
          session_application_id: session.application_id,
          found_app_data: !!appData,
          app_data_application_id: appData?.application_id,
          course_title: appData?.courses?.title
        });
        
        return {
          ...session,
          application_id: appData?.application_id,
          mentee: menteeProfile ? {
            id: menteeProfile.id,
            first_name: menteeProfile.first_name,
            last_name: menteeProfile.last_name,
            avatar_url: menteeProfile.avatar_url
          } : {
            id: session.mentee_id,
            first_name: '',
            last_name: '',
            avatar_url: ''
          },
          mentor_review: review,
          course: appData?.courses
        };
      }) || [];

      // Combine subscriptions with their reviews and mentee details
      const completedSubscriptionsWithReviews = subscriptionApplications?.map(subscription => {
        const review = subscriptionReviewsData.find(review => review.application_id === subscription.application_id);
        const menteeProfile = menteeProfilesData.find(profile => profile.id === subscription.user_id);
        
        return {
          id: subscription.id,
          application_id: subscription.application_id,
          subscription_end_date: subscription.subscription_end_date,
          course_title: subscription.courses?.title || 'Subscription',
          course_price: subscription.courses?.price || 0,
          mentee: {
            id: subscription.user_id,
            first_name: menteeProfile?.first_name || '',
            last_name: menteeProfile?.last_name || '',
            avatar_url: menteeProfile?.avatar_url
          },
          mentor_review: review
        };
      }) || [];

      console.log('DEBUG: Processed completed sessions with reviews:', completedSessionsWithReviews.length);
      console.log('DEBUG: Processed completed subscriptions with reviews:', completedSubscriptionsWithReviews.length);

      setCompletedSessions(completedSessionsWithReviews as any);
      setCompletedSubscriptions(completedSubscriptionsWithReviews);

      // Group both sessions and subscriptions by mentee
      const grouped: GroupedCompletedItems = {};
      
      // Add sessions to groups
      completedSessionsWithReviews.forEach(session => {
        const menteeId = (session.mentee as any)?.id || session.mentee_id;
        if (!grouped[menteeId]) {
          grouped[menteeId] = {
            mentee: session.mentee as any,
            sessions: [],
            subscriptions: []
          };
        }
        grouped[menteeId].sessions.push(session as any);
      });

      // Add subscriptions to groups
      completedSubscriptionsWithReviews.forEach(subscription => {
        const menteeId = subscription.mentee.id;
        if (!grouped[menteeId]) {
          grouped[menteeId] = {
            mentee: subscription.mentee,
            sessions: [],
            subscriptions: []
          };
        }
        grouped[menteeId].subscriptions.push(subscription);
      });

      console.log('DEBUG: Grouped completed items by mentee:', Object.keys(grouped).length, 'mentees');
      setGroupedCompletedItems(grouped);
      
    } catch (error) {
      console.error('ERROR: Fetching completed items:', error);
      toast({
        title: "Error",
        description: "Failed to load completed sessions and subscriptions.",
        variant: "destructive",
      });
    }
  };

  const handleApprove = async (applicationId: string) => {
    try {
      const { error } = await supabase
        .from('applications')
        .update({ status: 'pending_mentee_purchase' })
        .eq('id', applicationId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Application approved! Mentee can now proceed with payment.",
      });

      fetchApplications();
    } catch (error) {
      console.error("Error approving application:", error);
      toast({
        title: "Error",
        description: "Failed to approve application.",
        variant: "destructive",
      });
    }
  };

  const handleReject = async (applicationId: string) => {
    try {
      const { error } = await supabase
        .from('applications')
        .update({ status: 'rejected' })
        .eq('id', applicationId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Application rejected.",
      });

      fetchApplications();
    } catch (error) {
      console.error("Error rejecting application:", error);
      toast({
        title: "Error",
        description: "Failed to reject application.",
        variant: "destructive",
      });
    }
  };

  const handleViewProfile = (menteeId: string) => {
    if (!menteeId) {
      toast({
        title: "Error",
        description: "Unable to view profile - mentee ID not found.",
        variant: "destructive",
      });
      return;
    }
    navigate(`/mentee-profile/${menteeId}`);
  };

  const filterApplicationsByStatus = (status: string) => {
    if (status === 'awaiting_booking') {
      // Only include non-subscription applications to avoid duplicates
      const regularApps = applications.filter(app => 
        app.status === 'bookingRequired' && app.course?.course_type !== 'subscription'
      );
      return [...regularApps, ...subscriptionBookingCards];
    }
    return applications.filter(app => app.status === status);
  };

  const getTabCount = (status: string) => {
    if (status === 'completed') {
      // Count both sessions and subscriptions
      const totalSessions = Object.keys(groupedCompletedItems).reduce((total, menteeId) => 
        total + groupedCompletedItems[menteeId].sessions.length, 0
      );
      const totalSubscriptions = Object.keys(groupedCompletedItems).reduce((total, menteeId) => 
        total + groupedCompletedItems[menteeId].subscriptions.length, 0
      );
      return totalSessions + totalSubscriptions;
    }
    return filterApplicationsByStatus(status).length;
  };

  const toggleGroup = (menteeId: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [menteeId]: !prev[menteeId]
    }));
  };

  const toggleAllGroups = () => {
    const menteeIds = Object.keys(groupedCompletedItems);
    const newExpandedState = !allExpanded;
    
    const newExpandedGroups = menteeIds.reduce((acc, menteeId) => {
      acc[menteeId] = newExpandedState;
      return acc;
    }, {} as Record<string, boolean>);
    
    setExpandedGroups(newExpandedGroups);
    setAllExpanded(newExpandedState);
  };

  const renderApplicationCard = (application: Application) => (
    <Card key={application.id} className="p-4 sm:p-6 hover:shadow-md transition-shadow">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-3 sm:gap-4 mb-4">
            <Avatar className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0">
              <AvatarImage src={application.mentee.avatar_url} />
              <AvatarFallback>
                {application.mentee.first_name?.[0]}{application.mentee.last_name?.[0]}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
                <h3 className="font-semibold text-base sm:text-lg truncate">
                  {application.course.title}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {application.status === 'pending' && (
                    <Badge className="bg-yellow-100 text-yellow-800 text-xs">
                      Pending Approval
                    </Badge>
                  )}
                  {application.status === 'pending_mentee_purchase' && (
                    <Badge className="bg-blue-100 text-blue-800 text-xs">
                      Awaiting Payment
                    </Badge>
                  )}
                  {application.status === 'bookingRequired' && (
                    <Badge className="bg-purple-100 text-purple-800 text-xs">
                      {application.isSubscriptionCard ? 'Awaiting Booking' : 'Awaiting Booking'}
                    </Badge>
                  )}
                  {(application.status === 'paid' && application.has_booking) && (
                    <Badge className="bg-green-100 text-green-800 text-xs">
                      Booked
                    </Badge>
                  )}
                </div>
              </div>
              
              <p className="text-sm sm:text-base text-gray-600 mb-2 truncate">
                Mentee: {application.mentee.first_name} {application.mentee.last_name}
              </p>
              
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500 mb-3">
                {application.isSubscriptionCard && application.period_start_date && application.period_end_date && (
                  <>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                      Period: {new Date(application.period_start_date).toLocaleDateString()} – {new Date(application.period_end_date).toLocaleDateString()}
                    </span>
                    <span className="flex items-center gap-1">
                      <Repeat className="h-3 w-3 sm:h-4 sm:w-4" />
                      {application.total_allowance! - application.bookings_used!} slots remaining
                    </span>
                  </>
                )}
                {!application.isSubscriptionCard && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                    Applied {new Date(application.created_at).toLocaleDateString()}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <DollarSign className="h-3 w-3 sm:h-4 sm:w-4" />
                  ${application.course.price}
                </span>
                {application.remaining_sessions && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                    {application.remaining_sessions} sessions remaining
                  </span>
                )}
              </div>

              {application.message && !application.isSubscriptionCard && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs sm:text-sm text-gray-700 italic line-clamp-2">"{application.message}"</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto sm:ml-4">
          {application.status === 'pending' && (
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <Button 
                onClick={() => handleViewProfile(application.mentee.id)}
                variant="outline"
                size="sm"
                className="text-emu-blue hover:text-emu-blue/90 border-emu-blue hover:bg-emu-blue/10 min-h-[44px] text-xs sm:text-sm"
              >
                <User className="h-4 w-4 mr-1" />
                View Profile
              </Button>
              <Button 
                onClick={() => handleApprove(application.id)}
                className="bg-green-600 hover:bg-green-700 text-white min-h-[44px] text-xs sm:text-sm"
                size="sm"
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Approve
              </Button>
              <Button 
                onClick={() => handleReject(application.id)}
                variant="destructive"
                size="sm"
                className="min-h-[44px] text-xs sm:text-sm"
              >
                <X className="h-4 w-4 mr-1" />
                Decline
              </Button>
            </div>
          )}
          
          {application.status === 'pending_mentee_purchase' && (
            <div className="flex items-center text-blue-600">
              <Clock className="h-4 w-4 mr-2" />
              <span className="text-sm">Awaiting Payment</span>
            </div>
          )}

          {application.status === 'bookingRequired' && (
            <div className="flex items-center text-purple-600">
              <Calendar className="h-4 w-4 mr-2" />
              <span className="text-sm">
                {application.isSubscriptionCard ? 'Awaiting Booking' : 'Awaiting Booking'}
              </span>
            </div>
          )}

          {(application.status === 'paid' && application.has_booking) && (
            <div className="flex items-center text-green-600">
              <CheckCircle className="h-4 w-4 mr-2" />
              <span className="text-sm">Booked</span>
            </div>
          )}
        </div>
      </div>
    </Card>
  );

  const renderGroupedCompletedItems = () => {
    const menteeIds = Object.keys(groupedCompletedItems);
    
    if (menteeIds.length === 0) {
      return (
        <Card className="p-6 text-center">
          <p className="text-gray-500">No completed sessions or subscriptions</p>
        </Card>
      );
    }

    return (
      <div className="space-y-4">
        {/* Collapse/Expand Controls */}
        <div className="flex justify-end gap-2 mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={toggleAllGroups}
            className="text-sm"
          >
            {allExpanded ? (
              <>
                <ChevronUp className="h-4 w-4 mr-1" />
                Collapse All
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4 mr-1" />
                Expand All
              </>
            )}
          </Button>
        </div>

        {/* Grouped Items */}
        {menteeIds.map((menteeId) => {
          const { mentee, sessions, subscriptions } = groupedCompletedItems[menteeId];
          const isExpanded = expandedGroups[menteeId] || false;
          const totalItems = sessions.length + subscriptions.length;
          
          return (
            <Card key={menteeId} className="overflow-hidden">
              <Collapsible open={isExpanded} onOpenChange={() => toggleGroup(menteeId)}>
                <CollapsibleTrigger asChild>
                  <div className="p-6 cursor-pointer hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <Avatar className="w-12 h-12">
                          <AvatarImage src={mentee.avatar_url} />
                          <AvatarFallback className="bg-emu-blue text-white">
                            {mentee.first_name?.[0]}{mentee.last_name?.[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <h3 className="font-semibold text-lg text-gray-900">
                            {mentee.first_name} {mentee.last_name}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {sessions.length} session{sessions.length !== 1 ? 's' : ''} 
                            {subscriptions.length > 0 && `, ${subscriptions.length} subscription${subscriptions.length !== 1 ? 's' : ''}`} completed
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Completed
                        </span>
                        {isExpanded ? (
                          <ChevronUp className="h-5 w-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>
                </CollapsibleTrigger>
                
                <CollapsibleContent>
                  <div className="border-t bg-gray-50">
                    <div className="p-6 space-y-4">
                      {/* Render Sessions */}
                      {sessions.map((session) => (
                        <Card key={`session-${session.id}`} className="bg-white border border-gray-200">
                          <div className="p-6">
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex-1">
                                {session.course && (
                                  <h4 className="font-semibold text-lg text-gray-900 mb-2">
                                    {session.course.title}
                                  </h4>
                                )}
                                 <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                                   <span className="flex items-center gap-1">
                                     <Calendar className="h-4 w-4" />
                                     {new Date(session.booked_date).toLocaleDateString()}
                                   </span>
                                   <span className="flex items-center gap-1">
                                     <Clock className="h-4 w-4" />
                                     {session.start_time} - {session.end_time}
                                   </span>
                                    {session.course && (
                                      <span className="flex items-center gap-1">
                                        <DollarSign className="h-4 w-4" />
                                        {session.course.price}
                                      </span>
                                    )}
                                   </div>
                                 {session.mentee && (
                                   <p className="text-xs text-gray-500 mt-2">Mentee: {session.mentee.first_name} {session.mentee.last_name}</p>
                                 )}
                                 {session.application_id && (
                                   <p className="text-xs text-gray-500 mt-2">App ID: {session.application_id}</p>
                                 )}
                               </div>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Session Completed
                              </span>
                            </div>

                            {session.notes && (
                              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                                <p className="text-sm text-gray-700 leading-relaxed">{session.notes}</p>
                              </div>
                            )}

                            {/* Display mentor's review */}
                            {session.mentor_review && (
                              <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-emu-blue">
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className="text-sm font-semibold text-gray-900">Your Review</h4>
                                  <div className="flex items-center gap-1">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                      <Star
                                        key={star}
                                        className={`h-4 w-4 ${
                                          star <= session.mentor_review!.rating
                                            ? 'fill-yellow-400 text-yellow-400'
                                            : 'text-gray-300'
                                        }`}
                                      />
                                    ))}
                                  </div>
                                </div>
                                {session.mentor_review.review_text && (
                                  <p className="text-sm text-gray-700 mb-2 italic leading-relaxed">
                                    "{session.mentor_review.review_text}"
                                  </p>
                                )}
                                <p className="text-xs text-gray-500">
                                  Submitted on {new Date(session.mentor_review.created_at).toLocaleDateString()}
                                </p>
                              </div>
                            )}
                          </div>
                        </Card>
                      ))}

                      {/* Render Subscriptions */}
                      {subscriptions.map((subscription) => (
                        <Card key={`subscription-${subscription.id}`} className="bg-white border border-purple-200">
                          <div className="p-6">
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex-1">
                                <h4 className="font-semibold text-lg text-gray-900 mb-2">
                                  {subscription.course_title}
                                </h4>
                                <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                                  <span className="flex items-center gap-1">
                                    <Repeat className="h-4 w-4" />
                                    Subscription
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Calendar className="h-4 w-4" />
                                    Ended {new Date(subscription.subscription_end_date).toLocaleDateString()}
                                  </span>
                                   <span className="flex items-center gap-1">
                                     <DollarSign className="h-4 w-4" />
                                     {subscription.course_price}
                                   </span>
                                 </div>
                                 {subscription.mentee && (
                                   <p className="text-xs text-gray-500 mb-2">Mentee: {subscription.mentee.first_name} {subscription.mentee.last_name}</p>
                                 )}
                                 {subscription.application_id && (
                                   <p className="text-xs text-gray-500 mb-2">App ID: {subscription.application_id}</p>
                                 )}
                              </div>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Subscription Completed
                              </span>
                            </div>

                            {/* Display mentor's review */}
                            {subscription.mentor_review && (
                              <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-emu-blue">
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className="text-sm font-semibold text-gray-900">Your Review</h4>
                                  <div className="flex items-center gap-1">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                      <Star
                                        key={star}
                                        className={`h-4 w-4 ${
                                          star <= subscription.mentor_review!.rating
                                            ? 'fill-yellow-400 text-yellow-400'
                                            : 'text-gray-300'
                                        }`}
                                      />
                                    ))}
                                  </div>
                                </div>
                                {subscription.mentor_review.review_text && (
                                  <p className="text-sm text-gray-700 mb-2 italic leading-relaxed">
                                    "{subscription.mentor_review.review_text}"
                                  </p>
                                )}
                                <p className="text-xs text-gray-500">
                                  Submitted on {new Date(subscription.mentor_review.created_at).toLocaleDateString()}
                                </p>
                              </div>
                            )}
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="w-8 h-8 border-4 border-emu-blue border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-800">My Applications</h2>
      
      <Tabs defaultValue="pending" className="w-full">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 bg-gray-100 h-auto">
          <TabsTrigger 
            value="pending" 
            className="data-[state=active]:bg-white data-[state=active]:text-gray-900 text-xs sm:text-sm px-2 py-3"
          >
            <span className="sm:hidden">Pending ({getTabCount('pending')})</span>
            <span className="hidden sm:inline">Pending Approval ({getTabCount('pending')})</span>
          </TabsTrigger>
          <TabsTrigger 
            value="awaiting-payment"
            className="data-[state=active]:bg-white data-[state=active]:text-gray-900 text-xs sm:text-sm px-2 py-3"
          >
            <span className="sm:hidden">Payment ({getTabCount('pending_mentee_purchase')})</span>
            <span className="hidden sm:inline">Awaiting Payment ({getTabCount('pending_mentee_purchase')})</span>
          </TabsTrigger>
          <TabsTrigger 
            value="awaiting-booking"
            className="data-[state=active]:bg-white data-[state=active]:text-gray-900 text-xs sm:text-sm px-2 py-3"
          >
            <span className="sm:hidden">Booking ({getTabCount('awaiting_booking')})</span>
            <span className="hidden sm:inline">Awaiting Booking ({getTabCount('awaiting_booking')})</span>
          </TabsTrigger>
          <TabsTrigger 
            value="completed"
            className="data-[state=active]:bg-white data-[state=active]:text-gray-900 text-xs sm:text-sm px-2 py-3"
          >
            <span className="sm:hidden">Done ({getTabCount('completed')})</span>
            <span className="hidden sm:inline">Completed ({getTabCount('completed')})</span>
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="pending" className="space-y-4 mt-6">
          {filterApplicationsByStatus('pending').length === 0 ? (
            <Card className="p-6 text-center">
              <p className="text-gray-500">No applications pending approval</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {filterApplicationsByStatus('pending').map(renderApplicationCard)}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="awaiting-payment" className="space-y-4 mt-6">
          {filterApplicationsByStatus('pending_mentee_purchase').length === 0 ? (
            <Card className="p-6 text-center">
              <p className="text-gray-500">No applications awaiting payment</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {filterApplicationsByStatus('pending_mentee_purchase').map(renderApplicationCard)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="awaiting-booking" className="space-y-4 mt-6">
          {filterApplicationsByStatus('awaiting_booking').length === 0 ? (
            <Card className="p-6 text-center">
              <p className="text-gray-500">No applications awaiting booking</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {filterApplicationsByStatus('awaiting_booking').map(renderApplicationCard)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="completed" className="space-y-4 mt-6">
          {renderGroupedCompletedItems()}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MentorApplications;
